import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMediaQuery } from "@mui/material";

export const DASHBOARD_ROW_COUNT = 20;

export function computeDashboardRowLayout(
  datagridArea,
  theadH,
  { comfortableRows = false, forceScroll = false } = {}
) {
  const scrollRowHeight = comfortableRows ? 34 : 30;
  const minRowHeight = comfortableRows ? 26 : 22;
  const tbodyArea = Math.max(0, datagridArea - theadH);
  const fittedRowHeight = tbodyArea / DASHBOARD_ROW_COUNT;

  if (forceScroll) {
    return { needsScroll: true, rowHeight: scrollRowHeight };
  }

  const rowSlack = 2;
  const effectiveRowH = scrollRowHeight + rowSlack;
  const needsScroll =
    effectiveRowH * DASHBOARD_ROW_COUNT > tbodyArea + 0.5 ||
    (Number.isFinite(fittedRowHeight) &&
      fittedRowHeight < scrollRowHeight - 0.5);

  if (needsScroll) {
    return {
      needsScroll: true,
      rowHeight: scrollRowHeight,
    };
  }

  return {
    needsScroll: false,
    rowHeight:
      Number.isFinite(fittedRowHeight) && fittedRowHeight > 0
        ? fittedRowHeight
        : minRowHeight,
  };
}

export function shouldPreferDashboardScroll() {
  if (typeof window === "undefined") return false;
  return window.innerHeight < 960 || window.innerWidth <= 1400;
}

export function measureDashboardTableArea(dashboardRef) {
  const root = dashboardRef.current;
  if (!root) return null;

  const theadEl = root.querySelector(".div-datagrid thead");
  const theadH = theadEl?.getBoundingClientRect().height ?? 0;
  const containers = Array.from(
    root.querySelectorAll(".div-datagrid .table-container")
  );
  const containerHeights = containers
    .map((el) => el.clientHeight)
    .filter((h) => h > 24);

  if (containerHeights.length > 0) {
    return {
      datagridArea: Math.min(...containerHeights) - 2,
      theadH,
    };
  }

  const sections = Array.from(
    root.querySelectorAll(".nc-dashboard-table-section")
  );
  const sectionHeights = sections
    .map((section) => {
      const headerEl = section.querySelector(".nc-dashboard-header");
      const paginationEl = section.querySelector(".nc-dashboard-pagination");
      const sectionH = section.getBoundingClientRect().height;
      const headerH = headerEl?.getBoundingClientRect().height ?? 0;
      const paginationH = paginationEl?.getBoundingClientRect().height ?? 0;
      return Math.max(0, sectionH - headerH - paginationH);
    })
    .filter((h) => h > 24);

  if (sectionHeights.length > 0) {
    return {
      datagridArea: Math.min(...sectionHeights) - 4,
      theadH,
    };
  }

  const scrollHost =
    root.querySelector(".div-datagrid .table-container") ??
    root.querySelector(".div-datagrid");
  if (!scrollHost) return null;

  return {
    datagridArea: scrollHost.getBoundingClientRect().height,
    theadH,
  };
}

export default function useDashboardTableLayout({
  enabled,
  isLoading = false,
  isLoadingSecondary = false,
  comfortableRows = false,
}) {
  const isSmallScreen = useMediaQuery("(max-width: 768px)");
  const isMediumLayout = useMediaQuery("(max-width: 1200px)");
  const isCompactScreen = useMediaQuery("(max-width: 1400px)");
  const isDashboardFit = !isSmallScreen;
  const dashboardRef = useRef(null);
  const [dashboardVisibleRows, setDashboardVisibleRows] =
    useState(DASHBOARD_ROW_COUNT);
  const [dashboardNeedsScroll, setDashboardNeedsScroll] = useState(false);
  const largeRowHeight = comfortableRows ? 36 : 28;
  const [dashboardRowHeight, setDashboardRowHeight] = useState(largeRowHeight);

  const applyDashboardRowLayout = useCallback(
    (datagridArea, theadH) => {
      const { needsScroll, rowHeight } = computeDashboardRowLayout(
        datagridArea,
        theadH,
        {
          comfortableRows,
          forceScroll: shouldPreferDashboardScroll(),
        }
      );
      setDashboardNeedsScroll(needsScroll);
      setDashboardRowHeight(rowHeight);
    },
    [comfortableRows]
  );

  const recalcDashboardLayout = useCallback(() => {
    if (isSmallScreen) {
      setDashboardVisibleRows(DASHBOARD_ROW_COUNT);
      setDashboardNeedsScroll(false);
      setDashboardRowHeight(comfortableRows ? 36 : 28);
      return;
    }

    setDashboardVisibleRows(DASHBOARD_ROW_COUNT);

    const measured = measureDashboardTableArea(dashboardRef);
    if (measured) {
      applyDashboardRowLayout(measured.datagridArea, measured.theadH);
      return;
    }

    const top = dashboardRef.current?.getBoundingClientRect().top ?? 80;
    const available = window.innerHeight - top - 4;
    const headerH = comfortableRows ? 34 : 22;
    const theadH = isMediumLayout
      ? comfortableRows
        ? 28
        : 22
      : comfortableRows
        ? 36
        : 32;
    const paginationH = isMediumLayout ? 28 : comfortableRows ? 30 : 30;
    const sectionChrome = headerH + theadH + paginationH;
    const datagridArea = isMediumLayout
      ? (available - 2) / 2 - sectionChrome
      : available - sectionChrome;

    applyDashboardRowLayout(datagridArea, theadH);
  }, [isSmallScreen, isMediumLayout, comfortableRows, applyDashboardRowLayout]);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    recalcDashboardLayout();
    const rafId = requestAnimationFrame(() => {
      recalcDashboardLayout();
      requestAnimationFrame(recalcDashboardLayout);
    });
    const retryId = window.setTimeout(recalcDashboardLayout, 150);
    const lateRetryId = window.setTimeout(recalcDashboardLayout, 400);
    window.addEventListener("resize", recalcDashboardLayout);
    return () => {
      cancelAnimationFrame(rafId);
      window.clearTimeout(retryId);
      window.clearTimeout(lateRetryId);
      window.removeEventListener("resize", recalcDashboardLayout);
    };
  }, [enabled, recalcDashboardLayout, isLoading, isLoadingSecondary]);

  useLayoutEffect(() => {
    if (!enabled || isSmallScreen) return undefined;
    recalcDashboardLayout();
  }, [enabled, isSmallScreen, dashboardNeedsScroll, recalcDashboardLayout]);

  useLayoutEffect(() => {
    if (!enabled || isSmallScreen) return undefined;
    const root = dashboardRef.current;
    if (!root || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(() => {
      recalcDashboardLayout();
    });

    root
      .querySelectorAll(
        ".nc-dashboard-table-section, .div-datagrid, .div-datagrid .table-container"
      )
      .forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [enabled, isSmallScreen, recalcDashboardLayout]);

  useLayoutEffect(() => {
    if (!dashboardRef.current || isSmallScreen) return undefined;
    dashboardRef.current.style.setProperty(
      "--dashboard-row-height",
      `${dashboardRowHeight}px`
    );
  }, [dashboardRowHeight, isSmallScreen]);

  const dashboardClassName = isSmallScreen
    ? ` nc-dashboard--mobile${comfortableRows ? " nc-dashboard--comfortable" : ""}`
    : ` nc-dashboard--fit${isCompactScreen ? " nc-dashboard--compact" : ""}${
        isMediumLayout ? " nc-dashboard--stacked" : ""
      }${dashboardNeedsScroll ? " nc-dashboard--scroll-body" : ""}${
        comfortableRows ? " nc-dashboard--comfortable" : ""
      }`;

  const dashboardBoxSx = isDashboardFit
    ? {
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden",
        boxSizing: "border-box",
      }
    : undefined;

  return {
    dashboardRef,
    isSmallScreen,
    isMediumLayout,
    isDashboardFit,
    dashboardVisibleRows,
    dashboardRowHeight,
    dashboardClassName,
    dashboardBoxSx,
  };
}

export function useDashboardDisplayColumns(
  isSmallScreen,
  isMediumLayout,
  mobileFields,
  normalColumns,
  overdueColumns
) {
  const filterColumnsForMobile = useCallback(
    (columns) => {
      if (!isSmallScreen) return columns;
      return columns
        .filter((col) => mobileFields.has(col.field))
        .map((col) => ({
          ...col,
          rowCellStyle: {
            ...col.rowCellStyle,
            minWidth: 0,
            maxWidth: "none",
            width: "auto",
            margin: 0,
            whiteSpace: "normal",
            overflow: "visible",
            textOverflow: "clip",
            color: "#181818",
          },
        }));
    },
    [isSmallScreen, mobileFields]
  );

  const compactDashboardHeaderCellStyle = useCallback((headerCellStyle) => {
    if (!headerCellStyle) {
      return {
        display: "flex",
        alignItems: "center",
        minHeight: 0,
      };
    }
    const { height, fontSize, minHeight, maxHeight, ...rest } = headerCellStyle;
    return {
      ...rest,
      display: "flex",
      alignItems: "center",
      minHeight: 0,
      maxHeight: "none",
      height: "auto",
    };
  }, []);

  const compactDashboardRowCellStyle = useCallback(
    (rowCellStyle) => {
      const base = {
        minWidth: 0,
        maxWidth: "100%",
        width: "auto",
        margin: isMediumLayout ? "0 2px" : "0 4px",
        padding: 0,
        lineHeight: 1.15,
      };
      if (!rowCellStyle) return base;

      const { height, fontSize, padding, minWidth, maxWidth, margin, ...rest } =
        rowCellStyle;
      return { ...rest, ...base };
    },
    [isMediumLayout]
  );

  const normalizeDashboardColumns = useCallback(
    (columns) =>
      columns.map((col) => ({
        ...col,
        headerStyle: col.headerStyle
          ? {
              ...col.headerStyle,
              width: "auto",
              minWidth: 0,
              maxWidth: "100%",
            }
          : col.headerStyle,
        headerCellStyle: compactDashboardHeaderCellStyle(col.headerCellStyle),
        rowCellStyle: compactDashboardRowCellStyle(col.rowCellStyle),
      })),
    [
      isMediumLayout,
      compactDashboardHeaderCellStyle,
      compactDashboardRowCellStyle,
    ]
  );

  const displayNormal = useMemo(
    () =>
      isSmallScreen
        ? filterColumnsForMobile(normalColumns)
        : normalizeDashboardColumns(normalColumns),
    [isSmallScreen, normalColumns, filterColumnsForMobile, normalizeDashboardColumns]
  );

  const displayOverdue = useMemo(
    () =>
      isSmallScreen
        ? filterColumnsForMobile(overdueColumns)
        : normalizeDashboardColumns(overdueColumns),
    [isSmallScreen, overdueColumns, filterColumnsForMobile, normalizeDashboardColumns]
  );

  return { displayNormal, displayOverdue };
}

export function getDashboardTableSx(baseStyle) {
  return {
    ...baseStyle,
    width: "100%",
    maxWidth: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  };
}

/** CSS class suffixes when a dashboard table has no rows (shrink-wrap layout). */
export function getDashboardEmptyModifiers(isEmpty) {
  if (!isEmpty) {
    return { card: "", section: "", scroll: "" };
  }
  return {
    card: " nc-dashboard-card--empty",
    section: " nc-dashboard-table-section--empty",
    scroll: " nc-dashboard-scroll--empty",
  };
}

/** Shrink-wrap — only for non–viewport-fit layouts; fit mode uses NC stretch layout. */
export function getDashboardContentFitModifiers(
  hasRows,
  isLoading,
  useStretchLayout = false
) {
  if (useStretchLayout || isLoading || !hasRows) {
    return { card: "", section: "", scroll: "" };
  }
  return {
    card: " nc-dashboard-card--content-fit",
    section: " nc-dashboard-table-section--content-fit",
    scroll: " nc-dashboard-scroll--content-fit",
  };
}

/** Keep table body stretched while loading (do not shrink-wrap). */
export function getDashboardLoadingModifiers(isLoading) {
  if (!isLoading) {
    return { card: "", section: "", scroll: "" };
  }
  return {
    card: "",
    section: "",
    scroll: " nc-dashboard-scroll--loading",
  };
}

export function createDashboardRowClickHandler(onRowClick, { passRow = false } = {}) {
  return (row) => {
    if (!row?.applicationId) return;
    onRowClick(passRow ? row : row.applicationId);
  };
}
